import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  makeStyles,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import { Group } from './types';
import { groupColorService } from './groupColorService';

const useStyles = makeStyles((theme) => ({
  fieldGroupsContainer: {
    marginTop: 16,
    marginBottom: 16,
  },
  groupsTitle: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#333',
    marginBottom: 8,
  },
  groupItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    borderRadius: '4px',
    marginBottom: '4px',
  },
  groupText: {
    fontSize: '12px',
    color: '#333',
    flex: 1,
  },
  groupActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  actionButton: {
    padding: '2px',
    fontSize: '12px',
    minWidth: 'auto',
    '& .MuiSvgIcon-root': {
      fontSize: '14px',
    },
  },
  editInput: {
    '& .MuiInputBase-root': {
      fontSize: '12px',
      height: '24px',
    },
    '& .MuiInputBase-input': {
      padding: '2px 8px',
    },
  },
  newGroupInput: {
    marginBottom: '8px',
    '& .MuiInputBase-root': {
      fontSize: '12px',
      height: '32px',
    },
    '& .MuiInputBase-input': {
      padding: '4px 8px',
    },
  },
  addGroupButton: {
    fontSize: '12px',
    padding: '4px 12px',
    textTransform: 'none',
    backgroundColor: '#f0f0f0',
    color: '#666',
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
  },
}));

interface FieldGroupsProps {
  groups?: Group[];
  onGroupsChange?: (groups: Group[]) => void;
}

export const FieldGroups: React.FC<FieldGroupsProps> = ({
  groups = [
  ],
  onGroupsChange,
}) => {
  const classes = useStyles();
  const [localGroups, setLocalGroups] = useState<Group[]>(groups);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [nameError, setNameError] = useState('');

  const updateGroups = (newGroups: Group[]) => {
    setLocalGroups(newGroups);
    if (onGroupsChange) {
      onGroupsChange(newGroups);
    }
  };

  const handleEditStart = (group: Group) => {
    setEditingId(group.id);
    setEditingName(group.name);
  };

  const handleEditSave = () => {
    const trimmedName = editingName.trim();
    if (editingId && trimmedName) {
      // Check for duplicate names (excluding the current group being edited)
      const nameExists = localGroups.some(group => 
        group.id !== editingId && 
        group.name.toLowerCase() === trimmedName.toLowerCase()
      );
      
      if (nameExists) {
        // Could show an error message here - for now just return
        console.warn(`Group name "${trimmedName}" already exists`);
        return;
      }

      const updatedGroups = localGroups.map(group =>
        group.id === editingId
          ? { ...group, name: trimmedName }
          : group
      );
      updateGroups(updatedGroups);
    }
    setEditingId(null);
    setEditingName('');
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditingName('');
  };

  const handleDelete = (groupId: string) => {
    const updatedGroups = localGroups.filter(group => group.id !== groupId);
    updateGroups(updatedGroups);
  };

  const handleAddGroup = () => {
    const trimmedName = newGroupName.trim();
    if (trimmedName) {
      // Check for duplicate names
      const nameExists = localGroups.some(group => 
        group.name.toLowerCase() === trimmedName.toLowerCase()
      );
      
      if (nameExists) {
        setNameError(`Group name "${trimmedName}" already exists`);
        return;
      }

      // Clear any existing error
      setNameError('');

      const groupId = Date.now().toString();
      
      // Find the next available color that isn't already used
      const allColors = groupColorService.getAllColors();
      const usedColors = new Set(localGroups.map(group => group.color));
      
      let colorIndex = 0;
      let selectedColor = allColors[colorIndex];
      
      // Find the first unused color in sequence
      while (usedColors.has(selectedColor.primary) && colorIndex < allColors.length) {
        colorIndex++;
        selectedColor = allColors[colorIndex % allColors.length];
      }
      
      const newGroup: Group = {
        id: groupId,
        name: trimmedName,
        color: selectedColor.primary,
      };
      updateGroups([...localGroups, newGroup]);
      setNewGroupName('');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent, action: 'edit' | 'add') => {
    if (e.key === 'Enter') {
      if (action === 'edit') {
        handleEditSave();
      } else {
        handleAddGroup();
      }
    } else if (e.key === 'Escape' && action === 'edit') {
      handleEditCancel();
    }
  };

  return (
    <Box className={classes.fieldGroupsContainer}>
   
      
      {localGroups.map((group) => {
        const colorScheme = groupColorService.getGroupColor(group);
        return (
          <Box 
            key={group.id} 
            className={classes.groupItem}
            style={{
              backgroundColor: colorScheme.secondary,
              border: `1px solid ${colorScheme.border}`,
            }}
          >
            {editingId === group.id ? (
            <>
              <Box display="flex" alignItems="center" flex={1}>
                <Box
                  width={8}
                  height={8}
                  borderRadius="50%"
                  bgcolor={colorScheme.primary}
                  marginRight={1}
                  flexShrink={0}
                />
                <TextField
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  className={classes.editInput}
                  variant="outlined"
                  size="small"
                  autoFocus
                  onKeyDown={(e) => handleKeyPress(e, 'edit')}
                />
              </Box>
              <Box className={classes.groupActions}>
                <IconButton
                  className={classes.actionButton}
                  onClick={handleEditSave}
                  size="small"
                >
                  <CheckIcon />
                </IconButton>
                <IconButton
                  className={classes.actionButton}
                  onClick={handleEditCancel}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </>
          ) : (
            <>
              <Box display="flex" alignItems="center" flex={1}>
                <Box
                  width={8}
                  height={8}
                  borderRadius="50%"
                  bgcolor={colorScheme.primary}
                  marginRight={1}
                  flexShrink={0}
                />
                <Typography className={classes.groupText}>
                  {group.name}
                </Typography>
              </Box>
              <Box className={classes.groupActions}>
                <IconButton
                  className={classes.actionButton}
                  onClick={() => handleEditStart(group)}
                  size="small"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  className={classes.actionButton}
                  onClick={() => handleDelete(group.id)}
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </>
          )}
          </Box>
        );
      })}

      <TextField
        placeholder="Enter group name"
        value={newGroupName}
        onChange={(e) => {
          setNewGroupName(e.target.value);
          setNameError(''); // Clear error when user starts typing
        }}
        className={classes.newGroupInput}
        variant="outlined"
        size="small"
        fullWidth
        error={!!nameError}
        helperText={nameError}
        onKeyDown={(e) => handleKeyPress(e, 'add')}
      />

      <Button
        variant="outlined"
        className={classes.addGroupButton}
        onClick={handleAddGroup}
        disabled={!newGroupName.trim()}
        fullWidth
      >
        Add Field Group
      </Button>
    </Box>
  );
};

export default FieldGroups;